import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import numeral from 'numeral'
import { useMemo } from 'react'

import {
  ItemResaleRaffleUserDetailFragment,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { getItemSkuPrice } from '@/core/sku'

export * from './fragments'

export type State = {
  loading: boolean
  itemResaleRaffleUser?: Maybe<ItemResaleRaffleUserDetailFragment>
  raffledAtNext: string
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const ResaleApplicationCreateCompletionTemplate: Template<
  State,
  Action
> = ({ state }) => {
  const { t } = useTranslation('resaleApplicationCreateCompletion')
  const { routerPages, generatePath } = useRouter()

  return (
    <>
      <BaseLayout margin header={{ title: t('title', 'リセール応募完了') }}>
        <ResultCell />
        {!state.loading && (
          <DetailCell
            itemResaleRaffleUser={state.itemResaleRaffleUser}
            raffledAtNext={state.raffledAtNext}
          />
        )}
        <Stack alignItems="center" spacing={4} sx={{ pt: '44px', pb: 6 }}>
          <Button
            size="large"
            href={generatePath(routerPages.ResaleManagement, { tab: 'raffle' })}
            sx={{ width: 244, margin: 'auto' }}
            color="primary"
          >
            {t('resaleManagementScreen', 'リセール管理画面へ')}
          </Button>
          <Button
            variant="text"
            href={routerPages.Top}
            size="large"
            sx={{ width: 244, margin: 'auto' }}
            color="primary"
          >
            {t('topPage', 'TOPページへ')}
          </Button>
        </Stack>
      </BaseLayout>
    </>
  )
}

const ResultCell: Cell = () => {
  const { t } = useTranslation('resaleApplicationCreateCompletion')
  return (
    <CellFrame sx={{ pt: '55px', pb: 6 }}>
      <Stack alignItems="center" spacing={6}>
        <Typography variant="h5" align="center">
          {t('message', '応募が完了しました')}
        </Typography>
        <Typography variant="body2" align="center">
          {t('content')}
        </Typography>
      </Stack>
    </CellFrame>
  )
}

export const DetailCell = ({
  itemResaleRaffleUser,
  raffledAtNext,
}: {
  itemResaleRaffleUser: State['itemResaleRaffleUser']
  raffledAtNext: State['raffledAtNext']
}) => {
  const { t } = useTranslation('resaleApplicationCreateCompletion')
  const { price, formattedPrice } = useMemo(
    () => getItemSkuPrice(itemResaleRaffleUser?.item),
    [itemResaleRaffleUser?.item]
  )
  const data = [
    {
      label: t('ticketPrice', '価格'),
      value: `¥${formattedPrice ?? 0}/1${t('perSheet')}`,
    },
    {
      label: t('applicationNumber', '応募枚数'),
      value: t('applicationNumberValue', {
        number: String(itemResaleRaffleUser?.itemCount),
      }),
    },
    {
      label: t('totalAmount', '合計金額'),
      value: `¥${numeral(
        (itemResaleRaffleUser?.totalItemPrice ?? 0) +
          (itemResaleRaffleUser?.item?.metadata?.ticket?.resaleFee
            ?.purchaseFee ?? 0)
      ).format('0,0')}`,
    },
    {
      label: t('applicationDate', '応募日時'),
      value: itemResaleRaffleUser?.formattedCreatedAt,
    },
    { label: t('nextLotteryDate', '次回抽選日時'), value: raffledAtNext },
  ]
  return (
    <Box>
      <Card elevation={0}>
        <CardContent sx={{ p: 5, '&:last-child': { pb: 5 } }}>
          <Stack spacing={5}>
            <Stack spacing={1}>
              <Typography variant="h5">
                {itemResaleRaffleUser?.item?.title}
              </Typography>
              {!!itemResaleRaffleUser?.item?.sku?.unitName && (
                <Typography variant="subtitle1">
                  {itemResaleRaffleUser.item.sku.unitName}
                </Typography>
              )}
            </Stack>

            <Divider />
            <Stack spacing={6}>
              {data.map(({ label, value }, index) => (
                <Stack key={index} spacing={1}>
                  <Typography variant="subtitle1" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="body2">{value}</Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  )
}
